import { useState, useEffect } from 'react';
import { Modal, Button, Stack, Group, Text } from '@mantine/core';
import { useUpdateAsset } from '@/hooks/api/useAssets';
import { useOrganization } from '@/hooks/useOrganization';
import { notifications } from '@mantine/notifications';
import { DepreciationFormFields, type DepreciationFormValues } from './DepreciationFormFields';
import type { Asset } from '@/types/models';

interface DepreciationSetupModalProps {
  opened: boolean;
  onClose: () => void;
  asset: Asset | null;
  onSuccess?: () => void;
}

export function DepreciationSetupModal({
  opened,
  onClose,
  asset,
  onSuccess,
}: DepreciationSetupModalProps) {
  const { organizationId } = useOrganization();
  const updateAsset = useUpdateAsset();

  const [values, setValues] = useState<DepreciationFormValues>({
    depreciable_cost: null,
    salvage_value: null,
    asset_life_months: null,
    depreciation_method: 'straight-line',
    date_acquired: null,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof DepreciationFormValues, string>>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (opened && asset) {
      setValues({
        depreciable_cost: asset.depreciable_cost ?? asset.cost ?? null,
        salvage_value: asset.salvage_value ?? null,
        asset_life_months: asset.asset_life_months ?? null,
        depreciation_method: asset.depreciation_method ?? 'straight-line',
        date_acquired: asset.date_acquired ?? asset.purchase_date ?? null,
      });
      setErrors({});
    }
  }, [opened, asset]);

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const handleChange = <K extends keyof DepreciationFormValues>(
    field: K,
    value: DepreciationFormValues[K]
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof DepreciationFormValues, string>> = {};

    if (!values.depreciable_cost || values.depreciable_cost <= 0) {
      newErrors.depreciable_cost = 'Depreciable cost is required';
    }
    if (!values.asset_life_months || values.asset_life_months <= 0) {
      newErrors.asset_life_months = 'Asset life is required';
    }
    if (!values.depreciation_method) {
      newErrors.depreciation_method = 'Depreciation method is required';
    }
    if (!values.date_acquired) {
      newErrors.date_acquired = 'Date acquired is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!asset || !organizationId) return;
    if (!validate()) return;

    setIsLoading(true);
    try {
      await updateAsset.mutateAsync({
        id: asset.id,
        data: {
          is_depreciable: true,
          depreciable_cost: values.depreciable_cost,
          salvage_value: values.salvage_value ?? 0,
          asset_life_months: values.asset_life_months,
          depreciation_method: values.depreciation_method,
          date_acquired: values.date_acquired,
        },
        organizationId,
      });

      notifications.show({
        title: 'Depreciation Enabled',
        message: 'Depreciation tracking has been set up for this asset',
        color: 'green',
      });

      onSuccess?.();
      handleClose();
    } catch (error: any) {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to set up depreciation',
        color: 'red',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!asset) return null;

  return (
    <Modal opened={opened} onClose={handleClose} title="Set Up Depreciation" size="md">
      <Stack gap="md">
        <div>
          <Text size="sm" c="dimmed" mb="xs">
            Asset
          </Text>
          <Text fw={500}>
            {asset.tag_id} - {asset.description}
          </Text>
        </div>

        <DepreciationFormFields
          values={values}
          errors={errors}
          onChange={handleChange}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isLoading}>
            Enable Depreciation
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
